import { gql } from "@apollo/client";

export const ADDRESS_FRAGMENT = gql`
  fragment AddressFields on Address {
    street
    streetNumber
    streetComplement
    zip
    city
    country
  }
`;

export const RENTAL_ARTICLE_SUMMARY_FRAGMENT = gql`
  fragment RentalArticleSummaryFields on RentalAccommodationArticle {
    id
    slug
    title { value }
    images @limit(max: 10) { main aspectRatio assets { url breakpoint } }
    novelty
    recommended
    features { rooms maxGuests bedrooms }
    discounts {
      logic
      period
      type
      fixedValue
      percentageValue
    }
    merchant { id slug }
  }
`;

export const SEASONAL_ARTICLE_SUMMARY_FRAGMENT = gql`
  fragment SeasonalArticleSummaryFields on SeasonalAccommodationArticle {
    id
    slug
    title { value }
    images @limit(max: 10) { main aspectRatio assets { url breakpoint } }
    novelty
    recommended
    features { rooms maxGuests bedrooms }
    price { period isMonthly onDemand value }
    merchant { id slug }
  }
`;

export const ANNUAL_ARTICLE_SUMMARY_FRAGMENT = gql`
  fragment AnnualArticleSummaryFields on AnnualAccommodationArticle {
    id
    slug
    title { value }
    images @limit(max: 10) { main aspectRatio assets { url breakpoint } }
    novelty
    recommended
    features { livingSpace rooms parkings }
    price { availableAt onDemand value }
    merchant { id slug }
  }
`;

export const SALE_ARTICLE_SUMMARY_FRAGMENT = gql`
  fragment SaleArticleSummaryFields on SaleAccommodationArticle {
    id
    slug
    title { value }
    images @limit(max: 10) { main aspectRatio assets { url breakpoint } }
    novelty
    recommended
    sold
    features { livingSpace rooms parkings }
    price { onDemand value }
    merchant { id slug }
  }
`;

export const BOOKING_SUGGESTION_FRAGMENT = gql`
  fragment BookingSuggestionFields on AccommodationBookingOption {
    period
    bookable
    priceOnDemand
    price {
      amount
      detailed {
        bare
        discount
        providerCharge { amount adaptive }
      }
    }
  }
`;

export const ALL_FEATURES_FRAGMENT = gql`
  fragment AllFeaturesFields on RentalAccommodationFeatures {
    isPetCompliant
    isDisabledPersonCompliant
    withBalcony
    withTerrace
    withParking
    withWIFI
    withTV
    withDishwasher
    withWashingMachine
    withSauna
    withPool
    withFireplace
    withHotTub
    withHammam
    isSmokerCompliant
    withSmokeAlarm
    withCarbonAlarm
  }
`;

export const ALL_SEASONAL_FEATURES_FRAGMENT = gql`
  fragment AllSeasonalFeaturesFields on SeasonalAccommodationFeatures {
    isPetCompliant
    withBalcony
    withTerrace
    withParking
    withWIFI
    withTV
    withDishwasher
    withWashingMachine
    withSauna
    withPool
    withFireplace
  }
`;

export const ALL_ANNUAL_FEATURES_FRAGMENT = gql`
  fragment AllAnnualFeaturesFields on AnnualAccommodationFeatures {
    isNew
    isFurnished
    livingSpace
    rooms
    bedrooms
    bathrooms
    orientation
    floor { value matchLang }
    withDishwasher
    withWashingMachine
    withLaundryRoom
    withFireplace
    withPool
    withSauna
    withParking
    withGarage
    withBalcony
    withTerrace
    withOutdoorSpace
    withWIFI
    withTV
  }
`;

export const ALL_SALE_FEATURES_FRAGMENT = gql`
  fragment AllSaleFeaturesFields on SaleAccommodationFeatures {
    constructionYear
    isNew
    isFurnished
    canBeSecondaryHome
    isSellableToForeigners
    charges
    renovationFund
    livingSpace
    rooms
    bedrooms
    orientation
    floor { value matchLang }
    withDishwasher
    withWashingMachine
    withLaundryRoom
    withFireplace
    withPool
    withSauna
    withLift
    withParking
    withGarage
    withBalcony
    withTerrace
    withOutdoorSpace
    withWIFI
    withTV
  }
`;
export const COST_DESCRIPTIONS_FRAGMENT = gql`
  fragment AllCostDescriptionsFields on AccommodationBookingCostDescriptions {
    cleaning { matchLang value }
    bed { matchLang value }
    kitchen { matchLang value }
    bath { matchLang value}
    parking { matchLang value }
    pet { matchLang value }
    benefits { matchLang value }
    tax { matchLang value }
    deposit { matchLang value }
    charges { matchLang value }
    cancellation { matchLang value }
  }
`;

export const MERCHANT_CONTACT_FRAGMENT = (typeName: string) => gql`
  fragment ${typeName}Fields on ${typeName} {
    id
    slug
    name
    nameComplement
    address { ...AddressFields }
    phone
    fax
    email
    website
    logo
    lead { value }
  }
`;

export const CART_ITEM_FRAGMENT = gql`
  fragment CartItemFields on RentalAccommodationCartItem {
    itemId
    articleId
    title
    description
    image
    period
    price { bare discount options { type amount } amount }
    downPayment { rate providerCharge }
    expirationDate
  }
`;
